import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Navbar, Footer } from '../../components';
import api from '../../services/api';
import { formatCourseThumbnail, handleThumbnailError } from '../../utils/imageUtils';
import { formatDate, formatTime, formatShortDate, getCalendarParts } from '../../utils/dateUtils';
import { isValidPhone, sanitizePhone } from '../../utils/validationUtils';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/common/SEO';

const CourseDetailSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-64 bg-dark-200 rounded-lg mb-6" />
    <div className="h-8 bg-dark-200 rounded w-3/4 mb-4" />
    <div className="h-4 bg-dark-200 rounded w-full mb-2" />
    <div className="h-4 bg-dark-200 rounded w-5/6 mb-6" />
  </div>
);

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [enrollmentRequest, setEnrollmentRequest] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ name: '', email: '', phone: '' });
  const [enrollErrors, setEnrollErrors] = useState({});
  const [enrolling, setEnrolling] = useState(false);
  const [enrollSuccess, setEnrollSuccess] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      setError(null);
      if (!id) { setError('Course ID is required'); setLoading(false); return; }

      try {
        const data = await api.getCourse(id);
        setCourse(data.course);
      } catch (err) {
        console.error('Error fetching course:', err);
        setError(err.message || 'Course not found');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!id) return;
      try {
        const data = await api.getClassesByCourse(id);
        setClasses(data.classes || []);
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
    };
    fetchClasses();
  }, [id]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user || user.role !== 'student') return;
      setSubscriptionLoading(true);
      try {
        const data = await api.getMySubscription();
        setSubscription(data.subscription);
        setIsSubscriptionActive(data.isActive || false);
      } catch (err) {
        setSubscription(null);
        setIsSubscriptionActive(false);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    if (user) fetchSubscription();
  }, [user]);

  useEffect(() => {
    const checkRequest = async () => {
      if (!user || !id) return;
      try {
        const data = await api.checkEnrollmentRequest(id);
        setEnrollmentRequest(data.request);
      } catch (err) {
        // not critical
      }
    };
    if (user) checkRequest();
  }, [user, id]);

  const handleEnrollClick = () => {
    if (!user) {
      navigate('/login', { state: { from: `/courses/${id}` } });
      return;
    }
    setEnrollForm({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
    setShowEnrollModal(true);
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!user?.phone && !isValidPhone(enrollForm.phone)) errors.phone = 'Please enter a valid phone number (7-15 digits)';
    if (Object.keys(errors).length > 0) { setEnrollErrors(errors); return; }
    setEnrollErrors({});
    setEnrolling(true);
    try {
      const data = await api.createEnrollmentRequest({
        courseId: course._id,
        name: enrollForm.name,
        email: enrollForm.email,
        phone: enrollForm.phone,
      });
      setEnrollmentRequest(data.request);
      setShowEnrollModal(false);
      setEnrollSuccess(true);
    } catch (err) {
      alert(err.message || 'Failed to submit enrollment request');
    } finally {
      setEnrolling(false);
    }
  };

  const totalClassMinutes = classes.reduce((sum, cls) => sum + (cls.duration || 0), 0);
  const formatClassDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><CourseDetailSkeleton /></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!loading && (error || !course)) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-2xl font-bold text-dark-900 mb-4">Course Not Found</h1>
            <p className="text-dark-500 mb-6">{error || 'The course you are looking for does not exist.'}</p>
            <Link to="/courses" className="btn-primary inline-flex items-center gap-2">Browse All Courses</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const thumbnail = formatCourseThumbnail(course.thumbnail);

  const isAdmin = user && user.role === 'admin';
  const isEnrolledInCourse = isAdmin || (isSubscriptionActive && subscription && (
    !subscription.allowedCourses || subscription.allowedCourses.length === 0 ||
    subscription.allowedCourses.some(c => (c._id || c).toString() === id)
  ));

  const now = new Date();
  const sortedClasses = [...classes].sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  const upcomingClasses = sortedClasses.filter(c => new Date(c.scheduledDate) >= now);
  const pastClasses = sortedClasses.filter(c => new Date(c.scheduledDate) < now);

  const courseKeywords = [
    course.title,
    course.category,
    course.level,
    `${course.title} course`,
    `learn ${course.title}`,
    `${course.category} training`,
    'online course',
    'live classes',
  ].filter(Boolean);

  const courseStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.shortDescription || course.description,
    provider: {
      '@type': 'Organization',
      name: 'Lotus Arc Technologies',
      sameAs: window.location.origin,
    },
    ...(thumbnail && { image: thumbnail }),
    ...(course.level && {
      educationalLevel: course.level,
    }),
    ...(classes.length > 0 && {
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        instructor: classes[0]?.teacher?.name
          ? { '@type': 'Person', name: classes[0].teacher.name }
          : undefined,
      },
    }),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={course.title}
        description={course.shortDescription || course.description?.substring(0, 160)}
        ogImage={thumbnail}
        ogType="article"
        canonicalPath={`/courses/${id}`}
        keywords={courseKeywords}
        structuredData={courseStructuredData}
      />
      <Navbar />

      <main className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-dark-500 mb-6">
            <Link to="/" className="hover:text-primary-500">Home</Link>
            <span>/</span>
            <Link to="/courses" className="hover:text-primary-500">Courses</Link>
            <span>/</span>
            <span className="text-dark-900 line-clamp-1">{course.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-dark-900 mb-3 sm:mb-4">{course.title}</h1>
                <p className="text-base sm:text-lg text-dark-500 mb-4 sm:mb-6">{course.shortDescription || course.description}</p>

                <div className="flex flex-wrap gap-6 pb-6 border-b border-dark-100">
                  <div>
                    <p className="text-sm text-dark-500">Duration</p>
                    <p className="font-semibold text-dark-900">{totalClassMinutes > 0 ? formatClassDuration(totalClassMinutes) : '--'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-dark-500">Live Classes</p>
                    <p className="font-semibold text-dark-900">{classes.length}</p>
                  </div>
                  <div>
                    <p className="text-sm text-dark-500">Level</p>
                    <p className="font-semibold text-dark-900 capitalize">{course.level || 'All Levels'}</p>
                  </div>
                </div>
              </div>

              {/* Thumbnail */}
              <div className="aspect-video bg-dark-100 rounded-lg overflow-hidden">
                <img src={thumbnail} alt={course.title} onError={handleThumbnailError} className="w-full h-full object-cover" />
              </div>

              {/* About This Course */}
              {(course.aboutCourse || course.description) && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold text-dark-900 mb-4">About This Course</h2>
                  {course.aboutCourse ? (
                    <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.aboutCourse }} />
                  ) : (
                    <div className="prose max-w-none text-dark-600 whitespace-pre-line">{course.description}</div>
                  )}
                </div>
              )}

              {/* Live Classes */}
              {classes.length > 0 && (
                <div className="card overflow-hidden">
                  <div className="p-6 pb-0">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-dark-900">Live Classes</h2>
                      <span className="text-sm text-dark-400">{classes.length} class{classes.length !== 1 ? 'es' : ''}</span>
                    </div>
                    {isEnrolledInCourse && upcomingClasses.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-green-800">You have access to attend these classes. Click <strong>Join</strong> when a class is live.</span>
                      </div>
                    )}
                  </div>

                  {/* Upcoming Classes */}
                  {upcomingClasses.length > 0 && (
                    <div>
                      <div className="px-6 py-2">
                        <p className="text-xs font-semibold text-dark-400 uppercase tracking-wide">Upcoming</p>
                      </div>
                      <div className="divide-y divide-dark-100">
                        {upcomingClasses.map((cls, i) => {
                          const classDate = new Date(cls.scheduledDate);
                          const parts = getCalendarParts(cls.scheduledDate);
                          const isNext = i === 0;
                          return (
                            <div key={cls._id} className={`px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 ${isNext ? 'bg-primary-50/40' : 'hover:bg-dark-50'} transition-colors`}>
                              <div className="flex items-center sm:items-stretch gap-3 sm:block">
                                <div className={`flex-shrink-0 w-14 text-center rounded-lg py-1.5 ${isNext ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'}`}>
                                  <p className="text-xs font-medium leading-tight">{parts.month}</p>
                                  <p className="text-lg font-bold leading-tight">{parts.day}</p>
                                </div>
                                <h3 className="font-semibold text-dark-900 sm:hidden">
                                  {cls.title}
                                  {isNext && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium ml-2">Next</span>}
                                </h3>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="hidden sm:flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-dark-900">{cls.title}</h3>
                                  {isNext && <span className="text-xs bg-primary-100 text-primary-700 px-1.5 py-0.5 rounded font-medium">Next</span>}
                                </div>
                                <p className="text-sm text-dark-500 sm:mt-0.5">
                                  {parts.weekday}
                                  {' · '}
                                  {formatTime(cls.scheduledDate)}
                                  {cls.duration ? ` · ${cls.duration} min` : ''}
                                  {cls.teacher && typeof cls.teacher === 'object' ? ` · ${cls.teacher.name}` : ''}
                                </p>
                                {cls.description && (
                                  <div className="text-sm text-dark-600 mt-1.5 line-clamp-2" dangerouslySetInnerHTML={{ __html: cls.description }} />
                                )}
                              </div>
                              {isEnrolledInCourse ? (
                                cls.meetingLink ? (
                                  <a href={cls.meetingLink} target="_blank" rel="noopener noreferrer"
                                    className={`flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto ${
                                      isNext ? 'btn-primary' : 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                                    }`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                    Join
                                  </a>
                                ) : (
                                  <span className="flex-shrink-0 text-xs text-dark-400 py-2">Link coming soon</span>
                                )
                              ) : (
                                <span className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-dark-400 py-2">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                  Enroll to attend
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Past Classes */}
                  {pastClasses.length > 0 && (
                    <div className="border-t border-dark-100 px-6 py-3">
                      <details className="group">
                        <summary className="text-xs font-medium text-dark-400 cursor-pointer hover:text-dark-600 list-none flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          {pastClasses.length} past class{pastClasses.length !== 1 ? 'es' : ''}
                        </summary>
                        <div className="mt-2 divide-y divide-dark-50">
                          {pastClasses.map((cls) => {
                            const parts = getCalendarParts(cls.scheduledDate);
                            return (
                              <div key={cls._id} className="flex items-center gap-4 py-3 text-dark-400">
                                <span className="w-14 text-center text-xs flex-shrink-0">
                                  {parts.month} {parts.day}
                                </span>
                                <span className="text-sm">{cls.title}</span>
                                <span className="text-xs ml-auto">
                                  {formatTime(cls.scheduledDate)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
              )}

              {/* What You'll Learn */}
              {course.whatYouWillLearn && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold text-dark-900 mb-4">What You'll Learn</h2>
                  <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.whatYouWillLearn }} />
                </div>
              )}

              {/* Prerequisites */}
              {course.prerequisites && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold text-dark-900 mb-4">Prerequisites</h2>
                  <div className="prose max-w-none text-dark-600" dangerouslySetInnerHTML={{ __html: course.prerequisites }} />
                </div>
              )}

              {/* Legacy fallbacks for objectives/requirements arrays */}
              {!course.whatYouWillLearn && course.objectives && course.objectives.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold text-dark-900 mb-4">What You'll Learn</h2>
                  <ul className="space-y-2">
                    {course.objectives.map((obj, i) => (
                      <li key={i} className="flex items-start gap-3 text-dark-600">
                        <span className="text-primary-500 mt-1">&#10003;</span>
                        <span>{obj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!course.prerequisites && course.requirements && course.requirements.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold text-dark-900 mb-4">Requirements</h2>
                  <ul className="space-y-2">
                    {course.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-3 text-dark-600">
                        <span className="text-primary-500 mt-1">&bull;</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="card p-4 sm:p-6 lg:sticky lg:top-24">
                <img src={thumbnail} alt={course.title} onError={handleThumbnailError} className="w-full h-48 object-cover rounded-lg mb-6" />

                {/* Enrollment Status / Enroll Button */}
                {user && (user.role === 'student' || user.role === 'admin') && (
                  <div className="mb-6">
                    {isEnrolledInCourse ? (
                      <div className="p-4 rounded-lg border-2 border-green-400 bg-green-50">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-semibold text-green-700">{isAdmin ? 'Admin Access' : "You're Enrolled"}</span>
                        </div>
                        {!isAdmin && subscription && (
                          <p className="text-xs text-dark-600 text-center mb-3">
                            Access valid until {formatDate(subscription.endDate)}
                          </p>
                        )}
                        {upcomingClasses.length > 0 ? (
                          <div className="bg-white rounded-lg p-3 border border-green-200">
                            <p className="text-xs font-medium text-dark-500 mb-1">Next Live Class</p>
                            <p className="text-sm font-semibold text-dark-900">{upcomingClasses[0].title}</p>
                            <p className="text-xs text-dark-500 mt-0.5">
                              {formatShortDate(upcomingClasses[0].scheduledDate)}
                              {' at '}
                              {formatTime(upcomingClasses[0].scheduledDate)}
                            </p>
                            {upcomingClasses[0].meetingLink && (
                              <a href={upcomingClasses[0].meetingLink} target="_blank" rel="noopener noreferrer"
                                className="mt-2 btn-primary w-full flex items-center justify-center gap-2 py-2 text-sm rounded-lg">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                Join Now
                              </a>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-dark-400 text-center">No upcoming classes scheduled yet.</p>
                        )}
                      </div>
                    ) : enrollSuccess ? (
                      <div className="p-5 rounded-lg border-2 border-green-400 bg-green-50 text-center">
                        <svg className="w-12 h-12 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-base font-bold text-green-800 mb-2">Request Received!</h3>
                        <p className="text-sm text-dark-600 leading-relaxed">
                          Thank you for your interest! Our team will reach out to you via <strong>email</strong> or <strong>phone</strong> to complete the enrollment process.
                        </p>
                      </div>
                    ) : enrollmentRequest && enrollmentRequest.status !== 'rejected' && enrollmentRequest.status !== 'cancelled' ? (
                      <div className="p-5 rounded-lg border-2 border-amber-300 bg-amber-50 text-center">
                        <svg className="w-10 h-10 text-amber-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-sm font-bold text-amber-800 mb-1">
                          {enrollmentRequest.status === 'approved' ? 'Enrollment Approved!' : 'Enrollment Request Submitted'}
                        </h3>
                        <p className="text-xs text-dark-600 leading-relaxed">
                          {enrollmentRequest.status === 'approved'
                            ? 'Your enrollment has been approved. Your access will be activated shortly.'
                            : 'Your request has been received. Our team will reach out to you via email or phone to complete the enrollment.'}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <button
                          onClick={handleEnrollClick}
                          className="w-full btn-primary py-3 text-lg font-semibold rounded-lg"
                        >
                          Enroll Now
                        </button>
                        <p className="text-xs text-dark-400 text-center mt-2">Free enrollment — our team will activate your access</p>
                      </div>
                    )}
                  </div>
                )}

                {!user && (
                  <div className="mb-6">
                    <button
                      onClick={handleEnrollClick}
                      className="w-full btn-primary py-3 text-lg font-semibold rounded-lg"
                    >
                      Enroll Now
                    </button>
                    <p className="text-xs text-dark-500 text-center mt-2">Login required to enroll</p>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-dark-900 mb-2">Enroll in {course.title}</h2>
            <p className="text-sm text-dark-500 mb-6">Please provide your contact details. Our team will reach out to you via email or phone to complete the enrollment.</p>
            <form onSubmit={handleEnrollSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Full Name</label>
                <input type="text" value={enrollForm.name} readOnly disabled className="w-full px-4 py-2 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Email</label>
                <input type="email" value={enrollForm.email} readOnly disabled className="w-full px-4 py-2 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                {user?.phone ? (
                  <input type="tel" value={enrollForm.phone} readOnly disabled className="w-full px-4 py-2 border border-dark-200 rounded-lg bg-dark-50 text-dark-500 cursor-not-allowed" />
                ) : (
                  <>
                    <input type="tel" value={enrollForm.phone} onChange={(e) => { setEnrollForm(prev => ({ ...prev, phone: sanitizePhone(e.target.value) })); setEnrollErrors(prev => ({ ...prev, phone: null })); }} required className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${enrollErrors.phone ? 'border-red-400' : 'border-dark-200'}`} placeholder="+91 98765 43210" />
                    {enrollErrors.phone ? (
                      <p className="text-xs text-red-500 mt-1">{enrollErrors.phone}</p>
                    ) : (
                      <p className="text-xs text-dark-400 mt-1">Include country code, e.g. +91 98765 43210</p>
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-4 mt-6">
                <button type="button" onClick={() => setShowEnrollModal(false)} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg hover:bg-dark-50">
                  Cancel
                </button>
                <button type="submit" disabled={enrolling} className="flex-1 btn-primary px-4 py-2 disabled:opacity-50">
                  {enrolling ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CourseDetail;
